import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

export type MeterReads = Record<string | number, string | number | undefined>;
export type RegistersResponse = {
  registerId: number;
  valid: boolean;
};
type RegistersData = {
  valid: boolean;
  registers: RegistersResponse[];
};

export type MoveInMeterReadState = {
  isLoading: boolean;
  errors: NormalizedError[];
  data: RegistersData | null;
  details: {
    meterReads: MeterReads;
    attemptsCount: number;
  };
};

export const initialState: MoveInMeterReadState = {
  isLoading: false,
  errors: [],
  data: null,
  details: {
    meterReads: {},
    attemptsCount: 0
  }
};

export const slice = createSlice({
  name: 'moveInMeterRead',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(([key, value]) => {
        state[key] = value;
      });
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload.data;
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
    },
    setMeterRead: (state, { payload }) => {
      const { name, value } = payload;
      state.details.meterReads[name] = value;
    },
    incrementAttempts: (
      state,
      { payload }: PayloadAction<number | undefined>
    ) => {
      state.details.attemptsCount = payload
        ? payload
        : state.details.attemptsCount + 1;
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setError,
  setMeterRead,
  incrementAttempts
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectErrors = (state: RootState) => selectSlice(state).errors;
export const selectData = (state: RootState): RegistersData | null =>
  selectSlice(state).data;
export const selectDetails = (
  state: RootState
): MoveInMeterReadState['details'] => selectSlice(state).details;
export const selectMeterReads = (state: RootState): MeterReads =>
  selectDetails(state).meterReads;
export const selectRegisters = (state: RootState) =>
  selectData(state)?.registers;
export const selectAttemptsCount = (
  state: RootState
): MoveInMeterReadState['details']['attemptsCount'] =>
  selectDetails(state).attemptsCount;

export default slice.reducer;
